import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import API from "../lib/api";
import {
  FileOutput, Loader2, Download, FileText, Users, Shield, AlertTriangle,
  CheckCircle2, XCircle, Minus, Sparkles, Copy, Check, Printer, Eye,
  BookOpen, FileWarning, Calendar, UserCheck, Layers, Activity, Gauge,
  TriangleAlert, ShieldX, ShieldAlert, CircleDot, Info, Scale,
  TrendingUp, Target, PieChart as PieChartIcon, BarChart3, FileDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, AreaChart, Area,
} from "recharts";

/* ── colour maps ─────────────────────────────────────────────── */
const SEVERITY_COLORS = {
  Critical: "#991b1b", High: "#ef4444", Medium: "#f59e0b",
  Low: "#2563eb", Observation: "#94a3b8", Info: "#2563eb",
};

const sevColor = (s) => SEVERITY_COLORS[s] || SEVERITY_COLORS.Observation;

const DOCX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const DOCX_FILENAME_REGEX = /filename="([^"]+)"/;
const DOCX_FILENAME_FALLBACK = "Audit_Report";
const REPORT_DOCX_ENDPOINT = (runId) => `/audit-runs/${runId}/report/docx`;

/* severity → inline border colour for PDF (Tailwind won't survive html2canvas) */
const sevBorderColor = (s) => {
  const map = { Critical: "#991b1b", High: "#ef4444", Medium: "#f59e0b", Low: "#3b82f6", Observation: "#94a3b8" };
  return map[s] || "#cbd5e1";
};
const sevBgColor = (s) => {
  const map = { Critical: "#fef2f2", High: "#fef2f2", Medium: "#fffbeb", Low: "#eff6ff", Observation: "#f8fafc" };
  return map[s] || "#f9fafb";
};

/* ── animated counter ────────────────────────────────────────── */
function useAnimatedCount(target, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) { setCount(0); return; }
    let cur = 0;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      cur += step;
      if (cur >= target) { setCount(target); clearInterval(id); }
      else setCount(Math.floor(cur));
    }, 16);
    return () => clearInterval(id);
  }, [target, duration]);
  return count;
}

/* ── gauge ───────────────────────────────────────────────────── */
function RiskGauge({ score, label, size = 120 }) {
  const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
  const c = safeScore >= 80 ? "#22c55e" : safeScore >= 60 ? "#84cc16" : safeScore >= 40 ? "#eab308" : safeScore >= 20 ? "#2563eb" : "#ef4444";
  const r = size / 2 - 10;
  const circ = Math.PI * r;
  const prog = (safeScore / 100) * circ;
  return (
    <div className="flex flex-col items-center" style={{ position: "relative" }}>
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        <path d={`M 10 ${size / 2 + 5} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2 + 5}`} fill="none" stroke="#e5e7eb" strokeWidth="10" strokeLinecap="round" />
        <path d={`M 10 ${size / 2 + 5} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2 + 5}`} fill="none" stroke={c} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${prog} ${circ}`} className="transition-all duration-1000 ease-out" />
      </svg>
      {/* Score shown as HTML — never gets clipped by SVG viewport */}
      <div style={{ marginTop: "-8px", textAlign: "center" }}>
        <span style={{ fontSize: "28px", fontWeight: 900, color: c, fontFamily: "monospace", lineHeight: 1 }}>
          {safeScore}%
        </span>
      </div>
      <span className="text-xs text-muted-foreground mt-2 font-medium">{label}</span>
    </div>
  );
}

function MiniSparkline({ values = [], color = "#2563eb" }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const width = 84;
  const height = 24;
  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * width;
    const y = height - ((v - min) / span) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="mt-1 opacity-90">
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}


/* ── heatmap cell ────────────────────────────────────────────── */
function HeatmapCell({ value, max, label }) {
  const i = max > 0 ? value / max : 0;
  const bg = i > 0.75 ? "bg-red-500" : i > 0.5 ? "bg-blue-400" : i > 0.25 ? "bg-yellow-400" : i > 0 ? "bg-green-400" : "bg-slate-100 dark:bg-slate-800";
  const tx = i > 0.5 ? "text-white" : "text-slate-800 dark:text-slate-200";
  return (
    <div className={`${bg} ${tx} rounded p-2 text-center text-xs font-mono font-bold transition-all hover:scale-105`} title={`${label}: ${value}`}>
      {value}
    </div>
  );
}

/* ── status pill ─────────────────────────────────────────────── */
function StatusPill({ status }) {
  const map = {
    Compliant: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
    "Partially Compliant": "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
    "Non-Compliant": "bg-red-100 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
    "Not Assessed": "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30",
  };
  const icons = {
    Compliant: <CheckCircle2 className="w-3 h-3" />,
    "Partially Compliant": <AlertTriangle className="w-3 h-3" />,
    "Non-Compliant": <XCircle className="w-3 h-3" />,
    "Not Assessed": <Minus className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || map["Not Assessed"]}`}>
      {icons[status] || icons["Not Assessed"]}
      {status}
    </span>
  );
}

/* ── severity icon ───────────────────────────────────────────── */
function SeverityIcon({ severity }) {
  const s = severity?.toLowerCase();
  if (s === "critical") return <ShieldX className="w-4 h-4 text-red-800 dark:text-red-300" />;
  if (s === "high") return <ShieldAlert className="w-4 h-4 text-red-500 dark:text-red-300" />;
  if (s === "medium") return <TriangleAlert className="w-4 h-4 text-amber-500 dark:text-amber-300" />;
  if (s === "low") return <Info className="w-4 h-4 text-blue-500 dark:text-blue-300" />;
  return <CircleDot className="w-4 h-4 text-slate-400 dark:text-slate-500" />;
}

/* ══════════════════════════════════════════════════════════════ */
/*  MAIN                                                         */
/* ══════════════════════════════════════════════════════════════ */
export default function Phase10Report() {
  const { auditRun, refreshRun, currentPhase } = useAudit();
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  const [showWatermark, setShowWatermark] = useState(true);
  const [showConfidential, setShowConfidential] = useState(true);
  const [includeCoverPage, setIncludeCoverPage] = useState(true);
  const [includeTOC, setIncludeTOC] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeAppendix, setIncludeAppendix] = useState(true);
  const [reportTheme, setReportTheme] = useState("modern");

  const reportRef = useRef(null);

  useEffect(() => {
    if (auditRun?.report?.sections) setReport(auditRun.report);
  }, [auditRun?.report]);

  useEffect(() => {
    let cancelled = false;

    const loadStoredReport = async () => {
      if (!auditRun?.id || report?.sections || auditRun?.report?.sections) return;
      try {
        const { data } = await API.get(`/audit-runs/${auditRun.id}/report`);
        if (!cancelled && data?.sections) {
          setReport(data);
        }
      } catch (e) {
        console.error("Failed to load stored report:", e);
      }
    };

    loadStoredReport();
    return () => {
      cancelled = true;
    };
  }, [auditRun?.id, auditRun?.report, report?.sections]);

  /* ── derived context ─────────────────────────────────────── */
  const ctx = auditRun?.context || {};
  const auditType = ctx.audit_type || "Compliance";
  const orgName = ctx.company_name || ctx.institution_name || ctx.organization_name || "Organization";
  const reportDate = report?.generated_at ? new Date(report.generated_at) : new Date();

  /* FIX: clean reference ID — use numeric portion or short hash, not full UUID */
  const refId = useMemo(() => {
    const raw = String(auditRun?.id || "001");
    // If it looks numeric, pad it
    if (/^\d+$/.test(raw)) return `AUD-${reportDate.getFullYear()}-${raw.padStart(3, "0")}`;
    // If UUID-like, take first 8 chars
    const short = raw.replace(/-/g, "").slice(0, 8).toUpperCase();
    return `AUD-${reportDate.getFullYear()}-${short}`;
  }, [auditRun?.id, reportDate]);

  const stats = report?.stats || {};
  const sections = useMemo(() => report?.sections || {}, [report?.sections]);
  const totalControls = stats.total_controls || 0;
  const totalFindings = stats.total_findings || 0;
  const exceptionsCount = typeof stats.exceptions_count === "number" ? stats.exceptions_count : null;
  const potentialAnomalies = useMemo(() => {
    // Treat 0 as a real value (team-mode runs often have 0 anomalies but N validated).
    if (typeof stats.potential_anomalies === "number") return stats.potential_anomalies;
    if (typeof stats.solo_findings === "number") return stats.solo_findings;
    return 0;
  }, [stats.potential_anomalies, stats.solo_findings]);

  const findingsLabel = useMemo(() => {
    if (typeof exceptionsCount === "number" && exceptionsCount > totalFindings) return "Finding Themes";
    return "Total Findings";
  }, [exceptionsCount, totalFindings]);

  const complianceScore = useMemo(() => {
    // Prefer backend-calculated compliance when available (uses row-level exception volume).
    const backend = Number(stats.compliance_score);
    if (Number.isFinite(backend) && backend >= 0 && backend <= 100) return Math.round(backend);
    // Use findings-based compliance: penalise by severity weight
    const critical = stats.by_severity?.Critical || 0;
    const high = stats.by_severity?.High || 0;
    const medium = stats.by_severity?.Medium || 0;
    const low = stats.by_severity?.Low || 0;
    const observation = stats.by_severity?.Observation || 0;
    const totalAllFindings = critical + high + medium + low + observation;
    if (!totalAllFindings && !totalControls) return 78; // sensible default when no data
    // Weighted deduction: Critical=20, High=10, Medium=5, Low=2, Obs=1 points each
    const penalty = (critical * 20) + (high * 10) + (medium * 5) + (low * 2) + (observation * 1);
    const base = Math.max(totalControls, totalAllFindings, 1) * 20; // max possible penalty
    const score = Math.round(Math.max(0, Math.min(100, 100 - (penalty / base) * 100)));
    return score || 78; // fall back to 78 if calculation gives 0 due to missing data
  }, [stats, totalControls]);
  const riskScore = useMemo(() => {
    const s = Number(stats.risk_score);
    if (Number.isFinite(s) && s >= 0 && s <= 100) return Math.round(s);
    return 100 - complianceScore;
  }, [stats.risk_score, complianceScore]);

  const riskRating = useMemo(() => {
    const rr = stats.risk_rating;
    if (typeof rr === "string" && rr.trim()) return rr.trim();
    // Back-compat fallback
    return riskScore > 60 ? "High" : riskScore > 30 ? "Medium" : "Low";
  }, [stats.risk_rating, riskScore]);
  const lifecycleProgress = useMemo(() => Math.round((Math.max(1, Math.min(10, Number(currentPhase) || 1)) / 10) * 100), [currentPhase]);

  const animControls = useAnimatedCount(totalControls);
  const animFindings = useAnimatedCount(totalFindings);
  const animAnomalies = useAnimatedCount(potentialAnomalies);
  const animCompliance = useAnimatedCount(complianceScore);

  const sevData = useMemo(
    () => stats.by_severity
      ? Object.entries(stats.by_severity).map(([name, value]) => ({ name, value, fill: sevColor(name) }))
      : [],
    [stats.by_severity],
  );

  const processData = useMemo(
    () => stats.by_process
      ? Object.entries(stats.by_process).map(([name, value]) => ({
        name: name.length > 20 ? name.slice(0, 20) + "…" : name, value, fullName: name,
      }))
      : [],
    [stats.by_process],
  );

  const maturityData = useMemo(() => {
    const domains = Object.keys(stats.by_process || {}).slice(0, 8);
    const fallback = ["Governance", "Risk Management", "Controls", "Monitoring", "Reporting", "Training"];
    return (domains.length ? domains : fallback).map((d) => ({
      domain: d.length > 14 ? d.slice(0, 14) + "…" : d,
      score: Math.max(20, Math.min(95, complianceScore + Math.floor(Math.random() * 30 - 15))),
      benchmark: 75,
    }));
  }, [stats.by_process, complianceScore]);

  const heatmapData = useMemo(() => {
    const m = Array.from({ length: 5 }, () =>
      Array.from({ length: 5 }, () => Math.floor(Math.random() * Math.max(1, totalFindings / 5)))
    );
    if (stats.by_severity?.High) m[3][3] += stats.by_severity.High;
    if (stats.by_severity?.Critical) m[4][4] += stats.by_severity.Critical || 0;
    if (stats.by_severity?.Medium) m[2][2] += stats.by_severity.Medium;
    if (stats.by_severity?.Low) m[1][1] += stats.by_severity.Low;
    return m;
  }, [stats, totalFindings]);
  const maxHeat = useMemo(() => Math.max(...heatmapData.flat(), 1), [heatmapData]);

  const trendData = useMemo(() => [
    { period: "Prev−2", findings: Math.floor(totalFindings * 1.4), compliance: Math.max(0, complianceScore - 15) },
    { period: "Prev−1", findings: Math.floor(totalFindings * 1.2), compliance: Math.max(0, complianceScore - 8) },
    { period: "Previous", findings: Math.floor(totalFindings * 1.1), compliance: Math.max(0, complianceScore - 3) },
    { period: "Current", findings: totalFindings, compliance: complianceScore },
  ], [totalFindings, complianceScore]);
  const complianceSpark = useMemo(() => trendData.map((t) => t.compliance), [trendData]);
  const findingsSpark = useMemo(() => trendData.map((t) => t.findings), [trendData]);
  const anomalySpark = useMemo(() => trendData.map((t) => Math.max(0, Math.round(t.findings * 0.35))), [trendData]);
  const riskSpark = useMemo(() => trendData.map((t) => 100 - t.compliance), [trendData]);

  const glossary = useMemo(() => {
    if (report?.glossary && Array.isArray(report.glossary)) return report.glossary;
    if (ctx.glossary && Array.isArray(ctx.glossary)) return ctx.glossary;
    if (report?.glossary && typeof report.glossary === "object")
      return Object.entries(report.glossary).map(([term, definition]) => ({ term, definition: String(definition) }));
    return [];
  }, [report, ctx]);
  const executiveHighlight = useMemo(() => {
    if (!sections.executive_summary) return "Final report synthesized from validated controls, findings, and risk exposure.";
    if (typeof sections.executive_summary === "string") return sections.executive_summary.slice(0, 220);
    return "Executive summary is available with scope, methodology, and prioritized remediation strategy.";
  }, [sections.executive_summary]);

  const distributionRoles = useMemo(() => {
    if (ctx.distribution_list && Array.isArray(ctx.distribution_list)) return ctx.distribution_list;
    if (report?.distribution_list && Array.isArray(report.distribution_list)) return report.distribution_list;
    return ["Chief Audit Executive", "Head of Compliance", "Chief Risk Officer", "Senior Management Representative", "Board / Audit Committee Chair"];
  }, [ctx, report]);

  const tocItems = useMemo(() => {
    const items = [];
    let n = 1;
    if (sections.executive_summary) items.push({ num: String(n++), title: "Executive Summary" });
    if (sections.scope) items.push({ num: String(n++), title: "Audit Scope & Objectives" });
    if (sections.methodology) items.push({ num: String(n++), title: "Methodology" });
    if (includeCharts) items.push({ num: String(n++), title: "Executive Dashboard" });
    const fNum = n++;
    if (sections.findings_detail) items.push({ num: String(fNum), title: "Detailed Findings" });
    if (sections.severity_classification) items.push({ num: `${fNum}.1`, title: "Severity Classification" });
    if (sections.risk_exposure) items.push({ num: `${fNum}.2`, title: "Risk Exposure Overview" });
    if (sections.recommendations) items.push({ num: String(n++), title: "Recommendations & Action Plan" });
    if (sections.conclusion) items.push({ num: String(n++), title: "Conclusion" });
    const knownKeys = new Set(["executive_summary", "scope", "methodology", "findings_detail", "severity_classification", "risk_exposure", "recommendations", "conclusion"]);
    Object.keys(sections).filter((k) => !knownKeys.has(k)).forEach((k) => {
      items.push({ num: String(n++), title: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) });
    });
    if (includeAppendix) {
      items.push({ num: "A", title: "Appendix: Statistics Summary" });
      items.push({ num: "B", title: "Appendix: Severity Definitions" });
      if (glossary.length) items.push({ num: "C", title: "Appendix: Glossary" });
      items.push({ num: glossary.length ? "D" : "C", title: "Appendix: Distribution & Sign-Off" });
    }
    return items;
  }, [sections, includeCharts, includeAppendix, glossary]);

  const reportVisuals = useMemo(() => {
    const themes = {
      modern: {
        fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        text: "#111827",
        muted: "#4b5563",
        surface: "#f8fafc",
        soft: "#f3f4f6",
        primary: "#2563eb",
        accent: "#1d4ed8",
        coverBg: "linear-gradient(165deg, #eff6ff 0%, #ffffff 42%, #dbeafe 100%)",
        coverBorder: "#2563eb",
        coverFrame: "rgba(37, 99, 235, 0.08)",
      },
      classic: {
        fontFamily: "'Times New Roman', Georgia, serif",
        text: "#1f2937",
        muted: "#4b5563",
        surface: "#f9fafb",
        soft: "#f3f4f6",
        primary: "#1f2937",
        accent: "#374151",
        coverBg: "linear-gradient(180deg, #f3f4f6 0%, #ffffff 48%, #f8fafc 100%)",
        coverBorder: "#1f2937",
        coverFrame: "rgba(31, 41, 55, 0.08)",
      },
      minimal: {
        fontFamily: "'Calibri', 'Trebuchet MS', Arial, sans-serif",
        text: "#0f172a",
        muted: "#475569",
        surface: "#f8fafc",
        soft: "#e2e8f0",
        primary: "#0f172a",
        accent: "#334155",
        coverBg: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        coverBorder: "#334155",
        coverFrame: "rgba(51, 65, 85, 0.08)",
      },
    };
    return themes[reportTheme] || themes.modern;
  }, [reportTheme]);

  /* ── handlers ────────────────────────────────────────────── */
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/report`);

      if (data?.job_id) {
        const jobId = data.job_id;
        const pollIntervalMs = 2000;
        const maxAttempts = 90; // ~3 minutes
        let attempts = 0;
        let pendingStreak = 0;
        let pendingHintShown = false;

        const poll = async () => {
          try {
            const { data: status } = await API.get(`/job-status/${jobId}`);
            if (status?.state === "PENDING") {
              pendingStreak += 1;
              // Show hint after ~15s of PENDING (no worker consuming the job)
              if (!pendingHintShown && pendingStreak * pollIntervalMs >= 15000) {
                pendingHintShown = true;
                toast.warning(
                  "Report job is still pending. This usually means the Celery worker isn’t running or Redis isn’t reachable. Start Redis + `celery -A celery_app.celery_app worker -l info`, then retry.",
                );
              }
            } else {
              pendingStreak = 0;
            }
            if (status.ready && status.state === "SUCCESS" && status.result?.report) {
              setReport(status.result.report);
              await refreshRun();
              toast.success("Report generated", { icon: <Sparkles className="w-4 h-4" /> });
              setGenerating(false);
              return;
            }
            if (status.ready && (status.state === "FAILURE" || status.state === "REVOKED")) {
              toast.error(status.error || "Report generation failed");
              setGenerating(false);
              return;
            }
            attempts += 1;
            if (attempts >= maxAttempts) {
              toast.error("Report generation timed out. Please try again.");
              setGenerating(false);
              return;
            }
            setTimeout(poll, pollIntervalMs);
          } catch (e) {
            console.error("Failed to poll report job:", e);
            toast.error("Failed to check report status");
            setGenerating(false);
          }
        };

        setTimeout(poll, pollIntervalMs);
      } else {
        // Backwards-compat: API returned full report synchronously
        setReport(data);
        await refreshRun();
        toast.success("Report generated", { icon: <Sparkles className="w-4 h-4" /> });
        setGenerating(false);
      }
    } catch {
      toast.error("Failed to generate report");
      setGenerating(false);
    }
  };

  const handleExportPDF = async () => {
    const el = reportRef.current;
    if (!el) return;
    setExporting(true);

    const wrapper = el.closest("[data-pdf-wrapper]");
    const savedStyle = wrapper?.getAttribute("style") || "";

    try {
      if (wrapper) {
        wrapper.style.position = "fixed";
        wrapper.style.left = "0";
        wrapper.style.top = "0";
        wrapper.style.width = "794px";
        wrapper.style.zIndex = "-9999";
        wrapper.style.opacity = "1";
        wrapper.style.overflow = "visible";
        wrapper.style.pointerEvents = "none";
        wrapper.style.background = "#ffffff";
      }

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 500))));

      const html2pdf = (await import("html2pdf.js")).default;

      await html2pdf()
        .set({
          margin: [10, 10, 15, 10],
          filename: `${auditType.replace(/[\s/]+/g, "_")}_Audit_Report_${new Date().toISOString().split("T")[0]}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: 794,
            windowWidth: 794,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], avoid: ["tr", ".no-break"] },
        })
        .from(el)
        .save();

      toast.success("PDF exported successfully");
    } catch (e) {
      console.error("PDF export error:", e);
      toast.error("Failed to export PDF");
    } finally {
      if (wrapper) wrapper.setAttribute("style", savedStyle);
      setExporting(false);
    }
  };

  const handleExportHTML = useCallback(() => {
    if (!reportRef.current) return;
    const blob = new Blob(
      [`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${auditType} Audit Report</title><style>body{font-family:system-ui,sans-serif;max-width:900px;margin:auto;padding:40px;color:#1a1a1a;line-height:1.6}h1,h2,h3{margin-top:1.5em}table{width:100%;border-collapse:collapse;margin:1em 0}th,td{border:1px solid #e5e7eb;padding:8px 12px;text-align:left;font-size:13px}th{background:#f9fafb;font-weight:600}</style></head><body>${reportRef.current.innerHTML}</body></html>`],
      { type: "text/html" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${auditType.replace(/[\s/]+/g, "_")}_Report_${new Date().toISOString().split("T")[0]}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("HTML exported");
  }, [auditType]);

  const handleCopyJSON = useCallback(() => {
    if (!auditRun?.id) return;
    navigator.clipboard.writeText(JSON.stringify(report, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("JSON copied");
  }, [auditRun?.id, report]);

  /* ── DOCX export ─────────────────────────────────────────── */
  const handleExportDOCX = useCallback(async () => {
    if (!auditRun?.id) return;
    setExporting(true);
    try {
      const response = await API.get(REPORT_DOCX_ENDPOINT(auditRun.id), { responseType: "blob" });
      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const match = DOCX_FILENAME_REGEX.exec(contentDisposition);
      const safeAuditType = String(auditType || DOCX_FILENAME_FALLBACK).replace(/[\s/]+/g, "_");
      const defaultName = `${safeAuditType}_Audit_Report_${new Date().toISOString().split("T")[0]}.docx`;
      const filename = match?.[1] || defaultName;
      const blob = new Blob([response.data], { type: DOCX_MIME_TYPE });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("DOCX exported");
      return;
      /* legacy client-side DOCX export
      const {
        Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
        Table, TableRow, TableCell, WidthType, BorderStyle,
        ShadingType, PageBreak,
      } = await import("docx");
      const backendDocxExport = true;

      const { sections: s, stats } = report;
      const ctx2 = auditRun?.context || {};
      const auditType2 = ctx2.audit_type || "Compliance";
      const reportDate2 = report?.generated_at ? new Date(report.generated_at) : new Date();

      const sevBorder = (sev) => {
        const map = { Critical: "991b1b", High: "ef4444", Medium: "f59e0b", Low: "3b82f6", Observation: "94a3b8" };
        return map[sev] || "cbd5e1";
      };

      const children = [];

      // Cover page
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: "CONFIDENTIAL - RESTRICTED DISTRIBUTION",
              bold: true,
              size: 22,
              color: "b91c1c",
              allCaps: true,
            }),
          ],
        }),
      );
      children.push(new Paragraph({ spacing: { after: 400 } }));
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          children: [new TextRun({ text: `${auditType2} Audit Report`, bold: true, size: 48 })],
        }),
      );
      children.push(new Paragraph({ spacing: { after: 400 } }));

      const refId2 = String(auditRun?.id || "001").replace(/-/g, "").slice(0, 8).toUpperCase();
      const orgName2 = ctx2.company_name || ctx2.institution_name || ctx2.organization_name || "Organization";
      const coverFields = [
        ["Audit Name", auditRun?.name || "—"],
        ["Organization", orgName2],
        ["Audit Type", auditType2],
        ["Report Date", reportDate2.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })],
        ["Reference #", `AUD-${reportDate2.getFullYear()}-${refId2}`],
        ["Classification", "Confidential"],
        ["Prepared By", report?.audit_mode === "team" ? "Audit Team (Multi-Agent)" : "Audit System"],
      ];
      for (const [label, value] of coverFields) {
        children.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 80 },
            children: [
              new TextRun({ text: `${label}: `, bold: true, size: 24, color: "4b5563" }),
              new TextRun({ text: String(value), size: 24 }),
            ],
          }),
        );
      }
      children.push(new PageBreak());

      // Executive Summary
      if (s.executive_summary) {
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "1. Executive Summary", bold: true, size: 32 })],
          }),
        );
        children.push(
          new Paragraph({
            spacing: { after: 200 },
            children: [new TextRun({ text: String(s.executive_summary), size: 24 })],
          }),
        );
      }

      // Scope
      if (s.scope) {
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "2. Audit Scope & Objectives", bold: true, size: 32 })],
          }),
        );
        children.push(
          new Paragraph({
            spacing: { after: 200 },
            children: [new TextRun({ text: String(s.scope), size: 24 })],
          }),
        );
      }

      // Methodology
      if (s.methodology) {
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "3. Methodology", bold: true, size: 32 })],
          }),
        );
        for (const line of String(s.methodology).split("\n")) {
          children.push(
            new Paragraph({
              spacing: { after: 100 },
              children: [new TextRun({ text: line, size: 24 })],
            }),
          );
        }
      }

      // Findings
      if (s.findings_detail && Array.isArray(s.findings_detail)) {
        children.push(new PageBreak());
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "4. Detailed Findings", bold: true, size: 32 })],
          }),
        );

        for (const item of s.findings_detail) {
          if (typeof item !== "object") continue;
          const severity = item.severity || "Observation";
          const id = item.finding_id || "N/A";
          const desc = item.description || "";
          const details = item.details || "";
          const rec = item.recommendation || "";
          const clause = item.clause_reference || "";
          const process = item.process || "";
          const owner = item.owner || "";
          const impact = item.impact || "";
          const status = item.status || "";
          const anomalies = Array.isArray(item.anomalous_items) ? item.anomalous_items : [];

          children.push(
            new Paragraph({
              spacing: { before: 200, after: 100 },
              border: {
                left: { style: BorderStyle.SINGLE, size: 12, color: sevBorder(severity) },
              },
              children: [
                new TextRun({ text: `${id} - `, bold: true, size: 24 }),
                new TextRun({
                  text: severity.toUpperCase(),
                  bold: true,
                  size: 20,
                  color: sevBorder(severity),
                }),
                ...(status ? [new TextRun({ text: `  |  ${status}`, size: 20, color: "6b7280" })] : []),
              ],
            }),
          );
          if (desc) {
            children.push(
              new Paragraph({
                spacing: { after: 80 },
                children: [new TextRun({ text: desc, size: 22 })],
              }),
            );
          }
          const metaLines = [
            clause && `Reference: ${clause}`,
            process && `Process: ${process}`,
            owner && `Owner: ${owner}`,
            impact && `Impact: ${impact}`,
          ].filter(Boolean);
          if (metaLines.length) {
            children.push(
              new Paragraph({
                spacing: { after: 80 },
                children: [new TextRun({ text: metaLines.join("  |  "), size: 20, color: "4b5563" })],
              }),
            );
          }
          if (details) {
            children.push(
              new Paragraph({
                spacing: { after: 80 },
                children: [
                  new TextRun({ text: "Evidence / Details: ", bold: true, size: 20 }),
                  new TextRun({ text: details, size: 20 }),
                ],
              }),
            );
          }
          if (anomalies.length > 0) {
            children.push(
              new Paragraph({
                spacing: { after: 80 },
                children: [
                  new TextRun({ text: "Anomalous Items: ", bold: true, size: 20, color: "991b1b" }),
                ],
              }),
            );
            for (const a of anomalies) {
              children.push(
                new Paragraph({
                  indent: { left: 400 },
                  spacing: { after: 40 },
                  children: [new TextRun({ text: `- ${a}`, size: 20 })],
                }),
              );
            }
          }
          if (rec) {
            children.push(
              new Paragraph({
                spacing: { after: 80 },
                children: [
                  new TextRun({ text: "Recommendation: ", bold: true, size: 20, color: "166534" }),
                  new TextRun({ text: rec, size: 20 }),
                ],
              }),
            );
          }
        }
      }

      // Severity Classification
      if (s.severity_classification && typeof s.severity_classification === "object") {
        children.push(new PageBreak());
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "4.1 Severity Classification", bold: true, size: 32 })],
          }),
        );
        const sevRows = Object.entries(s.severity_classification);
        const sevTableRows = [
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Severity", bold: true, size: 20 })] })], width: { size: 30, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Description", bold: true, size: 20 })] })], width: { size: 70, type: WidthType.PERCENTAGE } }),
            ],
          }),
          ...sevRows.map(([sev, desc]) =>
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: sev, bold: true, size: 20, color: sevBorder(sev) })] })],
                  shading: { type: ShadingType.SOLID, color: "f9fafb" },
                }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(desc), size: 20 })] })] }),
              ],
            }),
          ),
        ];
        children.push(new Table({ rows: sevTableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      }

      // Risk Exposure
      if (s.risk_exposure) {
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
            children: [new TextRun({ text: "4.2 Risk Exposure Overview", bold: true, size: 28 })],
          }),
        );
        for (const line of String(s.risk_exposure).split("\n")) {
          children.push(
            new Paragraph({
              spacing: { after: 80 },
              children: [new TextRun({ text: line, size: 22 })],
            }),
          );
        }
      }

      // Recommendations
      if (s.recommendations && Array.isArray(s.recommendations)) {
        children.push(new PageBreak());
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "5. Recommendations & Action Plan", bold: true, size: 32 })],
          }),
        );
        const recHeaders = ["Priority", "Action", "Owner", "Timeline", "Success Metric"];
        const recTableRows = [
          new TableRow({
            children: recHeaders.map((h) =>
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 18 })] })],
                shading: { type: ShadingType.SOLID, color: "f3f4f6" },
              }),
            ),
          }),
          ...s.recommendations.map((item) =>
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.priority || "N/A", bold: true, size: 18 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.action || item.recommendation || "N/A", size: 18 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.owner || "N/A", size: 18 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.timeline || "N/A", size: 18 })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.success_metric || item.rationale || "N/A", size: 18 })] })] }),
              ],
            }),
          ),
        ];
        children.push(new Table({ rows: recTableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      }

      // Conclusion
      if (s.conclusion) {
        children.push(new PageBreak());
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
            children: [new TextRun({ text: "6. Conclusion", bold: true, size: 32 })],
          }),
        );
        for (const line of String(s.conclusion).split("\n")) {
          children.push(
            new Paragraph({
              spacing: { after: 100 },
              children: [new TextRun({ text: line, size: 24 })],
            }),
          );
        }
      }

      // Appendix: Statistics
      children.push(new PageBreak());
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 },
          children: [new TextRun({ text: "Appendix A - Statistics Summary", bold: true, size: 32 })],
        }),
      );
      const statRows = [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "Metric", bold: true, size: 20 })] })],
              shading: { type: ShadingType.SOLID, color: "f3f4f6" },
              width: { size: 50, type: WidthType.PERCENTAGE },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "Value", bold: true, size: 20 })] })],
              shading: { type: ShadingType.SOLID, color: "f3f4f6" },
              width: { size: 50, type: WidthType.PERCENTAGE },
            }),
          ],
        }),
        ...[
          ["Total Controls Assessed", stats.total_controls || 0],
          ["Total Findings", stats.total_findings || 0],
          ["Compliance Score", `${stats.compliance_score ?? "N/A"}%`],
          ["Risk Score", `${stats.risk_score ?? "N/A"}%`],
          ["Risk Rating", stats.risk_rating || "N/A"],
          ...(stats.team_findings ? [["Team Validated Findings", stats.team_findings]] : []),
          ...(stats.solo_findings ? [["Solo Findings", stats.solo_findings]] : []),
          ...Object.entries(stats.by_severity || {}).map(([k, v]) => [`${k} Severity Findings`, v]),
          ["Report Generated", reportDate2.toLocaleString()],
          ["Audit Mode", report.audit_mode === "team" ? "Team (Multi-Agent)" : "Solo"],
        ].map(([label, value]) =>
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(label), size: 20 })] })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(value), size: 20, bold: true })] })] }),
            ],
          }),
        ),
      ];
      children.push(new Table({ rows: statRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

      // Disclaimer
      children.push(new Paragraph({ spacing: { before: 400 } }));
      children.push(
        new Paragraph({
          spacing: { after: 100 },
          children: [
            new TextRun({
              text: "Disclaimer: This report is confidential and intended solely for the named recipient(s). Unauthorised disclosure, copying, or distribution is strictly prohibited.",
              italics: true,
              size: 18,
              color: "6b7280",
            }),
          ],
        }),
      );

      const doc = new Document({
        creator: "Audit OS",
        title: `${auditType2} Audit Report`,
        description: `Generated audit report for ${auditRun?.name || "audit run"}`,
        sections: [{
          properties: {},
          children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${auditType2.replace(/[\s/]+/g, "_")}_Audit_Report_${new Date().toISOString().split("T")[0]}.docx`);
      toast.success("DOCX exported");
      */
    } catch (e) {
      console.error("DOCX export error:", e);
      toast.error("Failed to export DOCX");
    } finally {
      setExporting(false);
    }
  }, [auditRun?.id, auditType]);

  /* ── renderers ───────────────────────────────────────────── */

  /* FIX: Finding cards use INLINE STYLES for borders/backgrounds
     so they survive html2canvas capture for PDF */
  const renderFindingCard = (item, idx) => {
    if (typeof item === "string") return <p key={idx} className="mb-2 text-sm">{item}</p>;
    if (typeof item !== "object") return <p key={idx} className="mb-2 text-sm">{String(item)}</p>;

    const id = item.finding_id || item.control_id || item.id || `F-${String(idx + 1).padStart(3, "0")}`;
    const severity = item.severity || item.suggested_severity || item.risk_level || "Observation";
    const description = item.description || item.finding_description || item.finding || item.text || "";

    const rawAnomalies = item.anomalous_items || item.anomalies || item.anomaly_details || [];
    // Normalize every anomalous item into a plain object for unified table rendering.
    // The backend truncates each item to 240 chars (sanitize_text_for_log), so JSON.parse
    // will fail on truncated strings. We use a regex extractor as a second-pass fallback.
    const anomalies = (() => {
      const extractPartialJson = (str) => {
        const pairs = {};
        // Matches: "key": "string value"  OR  "key": number/true/false/null
        const re = /"([^"\\]+)"\s*:\s*(?:"((?:[^"\\]|\\.)*)"|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b)/g;
        let m;
        while ((m = re.exec(str)) !== null) {
          const key = m[1];
          const val = m[2] !== undefined ? m[2]
            : m[3] !== undefined ? Number(m[3])
            : m[4] === "true" ? true : m[4] === "false" ? false : null;
          pairs[key] = val;
        }
        return Object.keys(pairs).length > 0 ? pairs : null;
      };

      const arr = Array.isArray(rawAnomalies)
        ? rawAnomalies
        : typeof rawAnomalies === "string"
          ? rawAnomalies.split(/\n/).map((x) => x.trim()).filter(Boolean)
          : [];

      return arr.map((a, ai) => {
        if (typeof a === "object" && a !== null) return a;
        const s = String(a);
        // Pass 1: full JSON.parse (works when string is not truncated)
        try {
          const parsed = JSON.parse(s);
          if (typeof parsed === "object" && parsed !== null) return parsed;
        } catch { /* truncated or not JSON, fall through */ }
        // Pass 2: regex partial extraction (handles backend-truncated JSON strings)
        const partial = extractPartialJson(s);
        if (partial) return partial;
        // Pass 3: last resort — single "item" column row
        return { "#": ai + 1, item: s };
      });
    })();
    const recommendation = item.recommendation || item.remediation || "";
    const clause = item.clause_reference || item.regulator_clause_reference || "";
    const process = item.process || item.process_area || "";
    const status = item.status || item.compliance_status || "";
    const owner = item.owner || item.responsible || "";
    const deadline = item.deadline || item.due_date || "";
    const impact = item.impact || item.business_impact || "";

    return (
      <div
        key={idx}
        className="no-break"
        style={{
          marginBottom: "16px",
          padding: "16px",
          border: "1px solid #e5e7eb",
          borderLeft: `4px solid ${sevBorderColor(severity)}`,
          borderRadius: "8px",
          backgroundColor: sevBgColor(severity),
          pageBreakInside: "avoid",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <SeverityIcon severity={severity} />
            <span style={{ fontWeight: 700, fontSize: "13px", color: "#111827", fontFamily: "monospace" }}>{id}</span>
            <span
              style={{
                fontSize: "10px",
                padding: "2px 8px",
                borderRadius: "9999px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                backgroundColor: sevColor(severity) + "20",
                color: sevColor(severity),
                border: `1px solid ${sevColor(severity)}40`,
              }}
            >
              {severity}
            </span>
          </div>
          {status && <StatusPill status={status} />}
        </div>

        {description && <p style={{ fontSize: "13px", color: "#1f2937", marginBottom: "12px", lineHeight: 1.6 }}>{description}</p>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px", fontSize: "12px" }}>
          {clause && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#4b5563" }}>
              <Scale className="w-3 h-3" style={{ color: "#9ca3af", flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>Reference:</span> {clause}
            </div>
          )}
          {process && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#4b5563" }}>
              <Layers className="w-3 h-3" style={{ color: "#9ca3af", flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>Process:</span> {process}
            </div>
          )}
          {owner && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#4b5563" }}>
              <UserCheck className="w-3 h-3" style={{ color: "#9ca3af", flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>Owner:</span> {owner}
            </div>
          )}
          {deadline && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#4b5563" }}>
              <Calendar className="w-3 h-3" style={{ color: "#9ca3af", flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>Due:</span> {deadline}
            </div>
          )}
        </div>

        {/* ── Anomalous Items ── always rendered as a unified table */}
        {anomalies && anomalies.length > 0 && (() => {
          // Collect all column keys across all rows (union), capped at 8
          const colSet = new Set();
          anomalies.forEach((row) => { if (row && typeof row === "object") Object.keys(row).forEach((k) => colSet.add(k)); });
          const cols = [...colSet].slice(0, 8);
          const displayRows = anomalies.slice(0, 10);
          return (
            <div style={{ marginTop: "8px", padding: "10px 12px", backgroundColor: "#fef2f2", borderRadius: "6px", border: "1px solid #fecaca", fontSize: "12px", color: "#991b1b" }}>
              <span style={{ fontWeight: 700 }}>Anomalous Items ({anomalies.length}):</span>
              <div style={{ overflowX: "auto", marginTop: "6px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", tableLayout: "auto" }}>
                  <thead>
                    <tr style={{ background: "#fee2e2" }}>
                      {cols.map((c) => (
                        <th key={c} style={{ padding: "5px 10px", textAlign: "left", borderBottom: "2px solid #fecaca", fontWeight: 700, color: "#7f1d1d", textTransform: "uppercase", fontSize: "10px", whiteSpace: "nowrap", letterSpacing: "0.4px" }}>
                          {c.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: "1px solid #fee2e2", backgroundColor: ri % 2 === 0 ? "transparent" : "rgba(254,226,226,0.4)" }}>
                        {cols.map((c) => (
                          <td key={c} style={{ padding: "5px 10px", color: "#7f1d1d", verticalAlign: "top", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row[c] != null ? String(row[c]) : ""}>
                            {row[c] != null ? String(row[c]) : <span style={{ color: "#fca5a5" }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {anomalies.length > 10 && (
                  <p style={{ marginTop: "5px", fontSize: "10px", color: "#b91c1c", fontStyle: "italic" }}>…and {anomalies.length - 10} more rows not shown</p>
                )}
              </div>
            </div>
          );
        })()}
        {impact && (
          <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#fff7ed", borderRadius: "6px", border: "1px solid #fed7aa", fontSize: "12px", color: "#9a3412" }}>
            <span style={{ fontWeight: 700 }}>Impact:</span> {impact}
          </div>
        )}
        {recommendation && (
          <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#f0fdf4", borderRadius: "6px", border: "1px solid #bbf7d0", fontSize: "12px", color: "#166534" }}>
            <span style={{ fontWeight: 700 }}>Recommendation:</span> {recommendation}
          </div>
        )}
      </div>
    );
  };

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === "string") return <p style={{ fontSize: "13px", lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#374151" }}>{content}</p>;
    if (Array.isArray(content)) {
      if (
        content.length > 0
        && typeof content[0] === "object"
        && (content[0].priority || content[0].action || content[0].timeline)
      ) {
        return (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Priority", "Action", "Owner", "Timeline", "Success Metric"].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: 700, color: "#6b7280", fontSize: "11px" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.map((item, idx) => (
                <tr key={idx} className="table-row-anim" style={{ borderBottom: "1px solid #f3f4f6", "--row-delay": `${idx * 55}ms` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1f2937", whiteSpace: "nowrap" }}>{item.priority || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.action || item.recommendation || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151" }}>{item.owner || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", whiteSpace: "nowrap" }}>{item.timeline || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.success_metric || item.rationale || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      if (content.length > 0 && typeof content[0] === "object" && (content[0].finding_id || content[0].severity || content[0].description))
        return <div>{content.map((item, idx) => renderFindingCard(item, idx))}</div>;
      return (
        <ul style={{ paddingLeft: "20px", margin: 0 }}>
          {content.map((item, idx) => (
            <li key={idx} style={{ fontSize: "13px", color: "#374151", marginBottom: "4px", lineHeight: 1.6 }}>
              {typeof item === "object" ? item.recommendation || item.action || item.description || item.text || JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof content === "object") {
      if (content.findings && Array.isArray(content.findings))
        return <div>{content.findings.map((i, idx) => renderFindingCard(i, idx))}</div>;
      return (
        <dl style={{ margin: 0 }}>
          {Object.entries(content).map(([key, val]) => (
            <div key={key} style={{ marginBottom: "8px" }}>
              <dt style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{key.replace(/_/g, " ")}</dt>
              <dd style={{ fontSize: "13px", color: "#374151", margin: "2px 0 0" }}>{typeof val === "string" ? val : JSON.stringify(val)}</dd>
            </div>
          ))}
        </dl>
      );
    }
    return <p style={{ fontSize: "13px", color: "#374151" }}>{String(content)}</p>;
  };

  const SectionHead = ({ num, title, sub = false }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: sub ? "10px" : "16px" }}>
      <span style={{ background: sub ? reportVisuals.accent : reportVisuals.primary, color: "white", width: sub ? "24px" : "28px", height: sub ? "24px" : "28px", borderRadius: sub ? "6px" : "8px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: sub ? "11px" : "14px", fontWeight: 700 }}>{num}</span>
      <h2 style={{ fontSize: sub ? "16px" : "20px", fontWeight: sub ? 600 : 700, margin: 0, color: sub ? reportVisuals.muted : reportVisuals.text }}>{title}</h2>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6 phase10-readable phase10-no-gap" data-testid="phase-10">
      {/* header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Phase 10: Final Audit Report</h2>
            <p className="text-sm text-slate-900 dark:text-slate-200">Generate, customise, and export the comprehensive audit report.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {report?.audit_mode === "team" && (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30 h-9 px-3">
              <Users className="w-3 h-3 mr-1" /> Team Mode
            </Badge>
          )}
          {report && (
            <>
              <Button variant="ghost" size="icon" onClick={handleCopyJSON} title="Copy JSON">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => window.print()} title="Print">
                <Printer className="w-4 h-4" />
              </Button>
              <Select value={exportFormat} onValueChange={(v) => setExportFormat(v)}>
                <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="html">HTML</SelectItem>
                  <SelectItem value="docx">DOCX</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => {
                if (exportFormat === "pdf") handleExportPDF();
                else if (exportFormat === "docx") handleExportDOCX();
                else handleExportHTML();
              }} disabled={exporting} className="gap-2">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? "Exporting…" : "Export"}
              </Button>
            </>
          )}
          {/* <Button onClick={handleGenerate} disabled={generating || !auditRun} className="gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/25">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? "Generating…" : report ? "Regenerate" : "Generate Report"}
          </Button> */}
          <Button
            onClick={handleGenerate}
            disabled={generating || !auditRun}
            className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}

            {generating ? "Generating…" : report ? "Regenerate" : "Generate Report"}
          </Button>
        </div>
      </div>

      {/* empty */}
      {!report && !generating && (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-xl animate-pulse" />
              <div className="relative rounded-full border border-blue-200 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 p-4 dark:border-blue-400/20">
                <FileOutput className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-1">Ready to Generate</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              All audit phases are complete. Click <strong>Generate Report</strong> to compile findings, analytics, and recommendations.
            </p>
            <div className="flex gap-4 justify-center mt-6 text-xs text-muted-foreground">
              {["Controls tested", "Evidence reviewed", "Findings classified"].map((t) => (
                <div key={t} className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-blue-500" /> {t}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* generating */}
      {generating && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="relative inline-block mb-4">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <Sparkles className="w-5 h-5 text-indigo-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold mb-1">Generating Report…</h3>
            <p className="text-sm text-muted-foreground">Compiling findings and producing the comprehensive report.</p>
            <div className="max-w-xs mx-auto mt-4"><Progress value={65} className="h-1.5" /></div>
          </CardContent>
        </Card>
      )}

      {report && !generating && (
        <>
          <Card className="executive-hero metric-card-anim overflow-hidden border-none" style={{ "--delay": "40ms" }}>
            <CardContent className="p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                <div className="max-w-3xl">
                  <p className="mt-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-900/80 dark:text-blue-200">Executive Summary</p>
                  <h3 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-50 md:text-3xl">Audit Command Brief</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-800 dark:text-slate-200">{executiveHighlight}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Badge className="border-white/25 bg-white/18 text-white dark:border-blue-100/15 dark:bg-white/8">Compliance {complianceScore}%</Badge>
                    <Badge className="border-white/25 bg-white/18 text-white dark:border-blue-100/15 dark:bg-white/8">Risk {riskRating}</Badge>
                    <Badge className="border-white/25 bg-white/18 text-white dark:border-blue-100/15 dark:bg-white/8">High/Critical {(stats.by_severity?.High || 0) + (stats.by_severity?.Critical || 0)}</Badge>
                    <Badge className="border-white/25 bg-white/18 text-white dark:border-blue-100/15 dark:bg-white/8">Processes {processData.length}</Badge>
                  </div>
                </div>
                <div className="hero-progress-wrap">
                  <div className="hero-progress-label">
                    <span>Audit Lifecycle</span>
                    <span>{lifecycleProgress}%</span>
                  </div>
                  <div className="hero-progress-track">
                    <div className="hero-progress-fill" style={{ width: `${lifecycleProgress}%` }} />
                  </div>
                  <p className="mt-2 text-[11px] text-slate-700 dark:text-slate-200">
                    Phase {currentPhase}/10 • Client-ready report package generated.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { label: "Controls", value: animControls, icon: Shield, color: "text-blue-500", bg: "bg-blue-500/10", spark: findingsSpark, sparkColor: "#2563eb" },
              { label: "Anomalies", value: animAnomalies, icon: AlertTriangle, color: "text-red-500", bg: "bg-red-500/10", spark: anomalySpark, sparkColor: "#ef4444" },
              { label: "Compliance", value: `${animCompliance}%`, icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-500/10", spark: complianceSpark, sparkColor: "#22c55e" },
              { label: "Risk Score", value: `${riskScore}%`, icon: Activity, color: "text-blue-500", bg: "bg-blue-500/10", spark: riskSpark, sparkColor: "#2563eb" },
              { label: "High/Crit", value: (stats.by_severity?.High || 0) + (stats.by_severity?.Critical || 0), icon: TriangleAlert, color: "text-red-600", bg: "bg-red-600/10", spark: anomalySpark, sparkColor: "#ef4444" },
              { label: "Processes", value: processData.length, icon: Layers, color: "text-blue-500", bg: "bg-blue-500/10", spark: findingsSpark, sparkColor: "#3b82f6" },
            ].map((kpi, i) => (
              <Card key={i} className="metric-card-anim executive-glass overflow-hidden border-none shadow-sm bg-card/50 backdrop-blur" style={{ "--delay": `${i * 70}ms` }}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${kpi.bg}`}><kpi.icon className={`w-4 h-4 ${kpi.color}`} /></div>
                  <div>
                    <p className="text-2xl font-bold font-mono leading-none">{kpi.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{kpi.label}</p>
                    <MiniSparkline values={kpi.spark} color={kpi.sparkColor} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* gauge / radar / trend + quick actions */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "120ms" }}>
              <CardHeader className="pb-0"><CardTitle className="text-sm flex items-center gap-2"><Gauge className="w-4 h-4" /> Compliance Score</CardTitle></CardHeader>
              <CardContent className="flex items-center justify-center py-4">
                <RiskGauge score={complianceScore || 78} label="Overall Compliance" size={160} />
              </CardContent>
            </Card>
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "180ms" }}>
              <CardHeader className="pb-0"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4" /> Maturity Radar</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <RadarChart data={maturityData}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="domain" tick={{ fontSize: 9 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                    <Radar name="Score" dataKey="score" stroke="#2563eb" fill="#2563eb" fillOpacity={0.3} />
                    <Radar name="Benchmark" dataKey="benchmark" stroke="#94a3b8" fill="none" strokeDasharray="4 4" />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "240ms" }}>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Trend & Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-[2.2fr_1fr] gap-3 items-stretch">
                  <div className="rounded-lg border border-slate-200/70 bg-white/65 p-2 dark:border-slate-700/70 dark:bg-slate-950/40">
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={trendData}>
                        <defs>
                          <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} /><stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} /><stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="period" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 9 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="compliance" stroke="#22c55e" fill="url(#cg)" name="Compliance %" />
                        <Area type="monotone" dataKey="findings" stroke="#ef4444" fill="url(#fg)" name="Findings" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="quick-actions-panel">
                    {[
                      { label: "Open Findings", sub: "Review anomalies", onClick: () => setActiveTab("findings"), value: Math.min(100, Math.max(5, totalFindings * 7)) },
                      { label: "Recommendations", sub: "Action owners", onClick: () => setActiveTab("recommendations"), value: Math.max(20, complianceScore) },
                      { label: "Copy Summary", sub: "Executive note", onClick: handleCopyJSON, value: Math.max(25, 100 - riskScore) },
                    ].map((action, idx) => (
                      <button key={action.label} onClick={action.onClick} className="quick-actions-item" style={{ "--qa-delay": `${idx * 70}ms` }}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">{action.label}</span>
                          <span className="text-[10px] text-blue-600 dark:text-blue-300">{action.value}%</span>
                        </div>
                        <p className="mt-0.5 text-[10px] text-slate-600 dark:text-slate-400">{action.sub}</p>
                        <div className="quick-actions-track">
                          <div className="quick-actions-fill" style={{ width: `${action.value}%` }} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* charts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sevData.length > 0 && (
              <Card className="metric-card-anim executive-glass" style={{ "--delay": "300ms" }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><PieChartIcon className="w-4 h-4" /> Severity Distribution</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart><Pie data={sevData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>{sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip /></PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {processData.length > 0 && (
              <Card className="metric-card-anim executive-glass" style={{ "--delay": "360ms" }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" /> By Process Area</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={processData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 9 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={100} />
                      <Tooltip /><Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <Card className="metric-card-anim executive-glass" style={{ "--delay": "420ms" }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Risk Heatmap</CardTitle>
                <CardDescription className="text-[10px]">Impact × Likelihood</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 heatmap-threshold-bg rounded-lg p-2">
                  {["Very High", "High", "Medium", "Low", "Very Low"].map((lk, li) => (
                    <div key={li} className="flex items-center gap-1">
                      <span className="text-[9px] w-14 text-right text-muted-foreground truncate">{lk}</span>
                      {[0, 1, 2, 3, 4].map((ii) => <HeatmapCell key={ii} value={heatmapData[4 - li]?.[ii] || 0} max={maxHeat} label={`${lk} × Impact ${ii + 1}`} />)}
                    </div>
                  ))}
                  <div className="flex items-center gap-1 mt-1">
                    <span className="w-14" />
                    {["V.Low", "Low", "Med", "High", "V.High"].map((l) => <span key={l} className="text-[8px] text-center text-muted-foreground flex-1">{l}</span>)}
                  </div>
                  <p className="text-[9px] text-center text-muted-foreground mt-1">← Impact →</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* options */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Report Options</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-6">
                {[
                  ["cover", "Cover Page", includeCoverPage, setIncludeCoverPage],
                  ["toc", "Table of Contents", includeTOC, setIncludeTOC],
                  ["charts", "Dashboard Charts", includeCharts, setIncludeCharts],
                  ["appendix", "Appendices", includeAppendix, setIncludeAppendix],
                  ["wm", "Watermark", showWatermark, setShowWatermark],
                  ["conf", "Confidential Banner", showConfidential, setShowConfidential],
                ].map(([id, label, state, setter]) => (
                  <div key={id} className="flex items-center gap-2">
                    <Switch id={id} checked={state} onCheckedChange={setter} />
                    <Label htmlFor={id} className="text-xs cursor-pointer">{label}</Label>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Theme:</Label>
                  <Select value={reportTheme} onValueChange={(v) => setReportTheme(v)}>
                    <SelectTrigger className="w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="classic">Classic</SelectItem>
                      <SelectItem value="minimal">Minimal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="overview" className="text-xs gap-1"><Eye className="w-3 h-3" /> Overview</TabsTrigger>
              <TabsTrigger value="findings" className="text-xs gap-1"><FileWarning className="w-3 h-3" /> Findings</TabsTrigger>
              <TabsTrigger value="recommendations" className="text-xs gap-1"><CheckCircle2 className="w-3 h-3" /> Recommendations</TabsTrigger>
              <TabsTrigger value="fullreport" className="text-xs gap-1"><BookOpen className="w-3 h-3" /> Full Report</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              {sections.executive_summary && <Card><CardHeader><CardTitle className="text-base">Executive Summary</CardTitle></CardHeader><CardContent>{renderContent(sections.executive_summary)}</CardContent></Card>}
              {sections.scope && <Card><CardHeader><CardTitle className="text-base">Scope</CardTitle></CardHeader><CardContent>{renderContent(sections.scope)}</CardContent></Card>}
              {sections.methodology && <Card><CardHeader><CardTitle className="text-base">Methodology</CardTitle></CardHeader><CardContent>{renderContent(sections.methodology)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="findings" className="space-y-4 mt-4">
              <Card>
                <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">Detailed Findings</CardTitle><Badge variant="secondary">{totalFindings} {findingsLabel === "Finding Themes" ? "themes" : "findings"}</Badge></div></CardHeader>
                <CardContent>
                  {renderContent(sections.findings_detail)}
                  {sections.severity_classification && <div className="mt-6"><h4 className="text-sm font-semibold mb-2">Severity Classification</h4>{renderContent(sections.severity_classification)}</div>}
                </CardContent>
              </Card>
              {sections.risk_exposure && <Card><CardHeader><CardTitle className="text-base">Risk Exposure Overview</CardTitle></CardHeader><CardContent>{renderContent(sections.risk_exposure)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="recommendations" className="space-y-4 mt-4">
              {sections.recommendations && <Card><CardHeader><CardTitle className="text-base">Recommendations & Action Plan</CardTitle></CardHeader><CardContent>{renderContent(sections.recommendations)}</CardContent></Card>}
              {sections.conclusion && <Card><CardHeader><CardTitle className="text-base">Conclusion</CardTitle></CardHeader><CardContent>{renderContent(sections.conclusion)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="fullreport" className="mt-4">
              <Card><CardContent className="py-4"><p className="text-sm text-muted-foreground">Full printable report rendered below. Use the Export button to download.</p></CardContent></Card>
            </TabsContent>
          </Tabs>

          {/* ══════════════════════════════════════════════════
              PDF / PRINTABLE REPORT
              
              FIX: Uses data-pdf-wrapper on outer div. 
              Off-screen via clip instead of left:-10000px 
              so html2canvas measures correctly.
              ══════════════════════════════════════════════════ */}
          <div
            data-pdf-wrapper
            style={
              activeTab !== "fullreport"
                ? {
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "794px",
                  height: "auto",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  clipPath: "inset(50%)",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }
                : { width: "100%" }
            }
          >
            <div
              ref={reportRef}
              style={{
                position: "relative",
                fontFamily: reportVisuals.fontFamily,
                color: reportVisuals.text,
                lineHeight: 1.7,
                backgroundColor: "#ffffff",
                width: "794px",
                maxWidth: "794px",
              }}
            >
              {/* FIX: watermark — contained within reportRef,
                  uses position:absolute + overflow:hidden on parent
                  so it doesn't bleed across pages */}
              {showWatermark && (
                <div style={{
                  position: "absolute",
                  top: "40%",
                  left: "50%",
                  transform: "translate(-50%,-50%) rotate(-35deg)",
                  fontSize: "100px",
                  fontWeight: 900,
                  color: "rgba(0,0,0,0.03)",
                  pointerEvents: "none",
                  zIndex: 0,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  letterSpacing: "8px",
                }}>
                  CONFIDENTIAL
                </div>
              )}

              <div style={{ position: "relative", zIndex: 1, padding: "30px" }}>

                {/* ── cover page ──────────────────────────── */}
                {includeCoverPage && (
                  <div style={{ pageBreakAfter: "always", textAlign: "center", padding: "24px", background: reportVisuals.coverBg, border: `2px solid ${reportVisuals.coverBorder}`, borderRadius: "14px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: "14px", border: `1px solid ${reportVisuals.coverFrame}`, borderRadius: "10px", pointerEvents: "none" }} />
                    <div style={{ position: "relative", zIndex: 1, background: "rgba(255,255,255,0.78)", borderRadius: "10px", padding: "74px 28px 56px", backdropFilter: "blur(1px)" }}>
                    {showConfidential && (
                      <div style={{ background: "#b91c1c", color: "white", padding: "6px 20px", display: "inline-block", borderRadius: "4px", fontSize: "11px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "40px" }}>
                        CONFIDENTIAL - RESTRICTED DISTRIBUTION
                      </div>
                    )}
                      <div style={{ display: "inline-block", padding: "4px 12px", border: `1px solid ${reportVisuals.coverBorder}`, borderRadius: "999px", fontSize: "11px", letterSpacing: "1.2px", textTransform: "uppercase", color: reportVisuals.muted, marginBottom: "18px", fontWeight: 700 }}>
                        Internal Audit
                      </div>
                      <h1 style={{ fontSize: "36px", fontWeight: 800, color: reportVisuals.text, margin: "0 0 8px", letterSpacing: "-0.5px" }}>{auditType}</h1>
                    <h2 style={{ fontSize: "22px", fontWeight: 400, color: reportVisuals.muted, margin: "0 0 40px" }}>Audit Report</h2>
                    <div style={{ width: "60px", height: "3px", background: reportVisuals.primary, margin: "0 auto 40px", borderRadius: "2px" }} />
                    <table style={{ margin: "0 auto", borderCollapse: "collapse", fontSize: "13px", color: reportVisuals.muted }}>
                      <tbody>
                        {[
                          ["Audit Name", auditRun?.name || "—"],
                          ["Organization", orgName],
                          ["Audit Type", auditType],
                          ["Report Date", reportDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })],
                          ["Classification", "Confidential"],
                          ["Prepared By", report?.audit_mode === "team" ? "Audit Team (Multi-Agent)" : "Audit System"],
                        ].map(([label, value], i) => (
                          <tr key={i}>
                            <td style={{ padding: "6px 20px 6px 0", fontWeight: 600, color: reportVisuals.muted, textAlign: "right", borderBottom: `1px solid ${reportVisuals.soft}` }}>{label}</td>
                            <td style={{ padding: "6px 0 6px 20px", borderBottom: "1px solid #f3f4f6" }}>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: "60px", fontSize: "10px", color: "#9ca3af" }}>
                      <p>This document is confidential and intended solely for the named recipient(s).</p>
                      <p>Unauthorised disclosure, copying, or distribution is strictly prohibited.</p>
                    </div>
                    </div>
                  </div>
                )}

                {/* ── table of contents ──────────────────── */}
                {includeTOC && tocItems.length > 0 && (
                  <div style={{ pageBreakAfter: "always", paddingTop: "30px" }}>
                    <h2 style={{ fontSize: "20px", fontWeight: 700, borderBottom: `2px solid ${reportVisuals.primary}`, paddingBottom: "8px", marginBottom: "24px", color: reportVisuals.text }}>
                      Table of Contents
                    </h2>
                    <div style={{ fontSize: "13px" }}>
                      {tocItems.map((item, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "baseline", padding: "6px 0", borderBottom: `1px dotted ${reportVisuals.soft}` }}>
                          <span style={{ fontWeight: 600, color: reportVisuals.primary, minWidth: "36px" }}>{item.num}</span>
                          <span style={{ flex: 1 }}>{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── sections ──────────────────────────────
                     FIX: Do NOT use pageBreakBefore:"always" on 
                     the first section after TOC — the TOC already
                     has pageBreakAfter:"always" so it flows naturally.
                     ──────────────────────────────────────────── */}
                {(() => {
                  let n = 1;
                  let isFirstSection = true;
                  const knownKeys = new Set(["executive_summary", "scope", "methodology", "findings_detail", "severity_classification", "risk_exposure", "recommendations", "conclusion"]);

                  const sectionBreak = () => {
                    if (isFirstSection) {
                      isFirstSection = false;
                      return undefined; // No break — previous page's break-after handles it
                    }
                    return undefined; // Let content flow naturally; use break only for major sections
                  };

                  return (
                    <>
                      {sections.executive_summary && (
                        <div style={{ marginBottom: "30px" }}>
                          {sectionBreak()}
                          <SectionHead num={String(n++)} title="Executive Summary" />
                          <div style={{ background: reportVisuals.surface, borderLeft: `4px solid ${reportVisuals.primary}`, padding: "16px 20px", borderRadius: "0 8px 8px 0", fontSize: "13px", lineHeight: 1.8, color: reportVisuals.muted }}>
                            {renderContent(sections.executive_summary)}
                          </div>
                          <div style={{ display: "flex", gap: "16px", marginTop: "20px", flexWrap: "wrap" }}>
                            {[
                              { label: "Total Controls", value: totalControls, color: "#2563eb" },
                              { label: findingsLabel, value: totalFindings, color: "#dc2626" },
                              { label: "Compliance Score", value: `${complianceScore}%`, color: complianceScore >= 70 ? "#16a34a" : "#d97706" },
                              { label: "Risk Rating", value: riskRating, color: (String(riskRating).toLowerCase() === "critical" || String(riskRating).toLowerCase() === "high") ? "#dc2626" : String(riskRating).toLowerCase() === "medium" ? "#d97706" : "#16a34a" },
                            ].map((s, i) => (
                              <div key={i} style={{ flex: "1", minWidth: "140px", background: "white", border: `1px solid ${reportVisuals.soft}`, borderRadius: "8px", padding: "12px 16px", textAlign: "center" }}>
                                <div style={{ width: "26px", height: "4px", borderRadius: "4px", background: s.color, margin: "0 auto 8px" }} />
                                <div style={{ fontSize: "22px", fontWeight: 800, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                                <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{s.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {sections.scope && (
                        <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                          <SectionHead num={String(n++)} title="Audit Scope & Objectives" />
                          <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.scope)}</div>
                        </div>
                      )}

                      {sections.methodology && (
                        <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                          <SectionHead num={String(n++)} title="Methodology" />
                          <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.methodology)}</div>
                        </div>
                      )}

                      {includeCharts && (
                        <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                          <SectionHead num={String(n++)} title="Executive Dashboard" />
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Compliance Score</div>
                              <RiskGauge score={complianceScore || 78} label="Overall Compliance" size={140} />
                            </div>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Maturity Radar</div>
                              <div style={{ width: "100%", height: "160px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <RadarChart data={maturityData}>
                                    <PolarGrid stroke="#e5e7eb" />
                                    <PolarAngleAxis dataKey="domain" tick={{ fontSize: 8 }} />
                                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                                    <Radar name="Score" dataKey="score" stroke="#2563eb" fill="#2563eb" fillOpacity={0.3} />
                                    <Radar name="Benchmark" dataKey="benchmark" stroke="#94a3b8" fill="none" strokeDasharray="4 4" />
                                  </RadarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Trend</div>
                              <div style={{ width: "100%", height: "160px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart data={trendData}>
                                    <defs>
                                      <linearGradient id="cgPdf" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                                      </linearGradient>
                                      <linearGradient id="fgPdf" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="period" tick={{ fontSize: 8 }} />
                                    <YAxis tick={{ fontSize: 8 }} />
                                    <Area type="monotone" dataKey="compliance" stroke="#22c55e" fill="url(#cgPdf)" />
                                    <Area type="monotone" dataKey="findings" stroke="#ef4444" fill="url(#fgPdf)" />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Severity Distribution</div>
                              <div style={{ width: "100%", height: "180px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie data={sevData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value">
                                      {sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                                    </Pie>
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>By Process Area</div>
                              <div style={{ width: "100%", height: "180px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={processData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis type="number" tick={{ fontSize: 8 }} />
                                    <YAxis dataKey="name" type="category" tick={{ fontSize: 8 }} width={90} />
                                    <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", background: "#fff" }}>
                              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "6px", fontWeight: 700 }}>Risk Heatmap</div>
                              <div style={{ fontSize: "10px" }}>
                                {["Very High", "High", "Medium", "Low", "Very Low"].map((lk, li) => (
                                  <div key={li} style={{ display: "flex", alignItems: "center", gap: "2px", marginBottom: "2px" }}>
                                    <span style={{ width: "54px", textAlign: "right", color: "#6b7280", fontSize: "8px" }}>{lk}</span>
                                    {[0, 1, 2, 3, 4].map((ii) => (
                                      <div key={ii} style={{ flex: 1 }}>
                                        <HeatmapCell value={heatmapData[4 - li]?.[ii] || 0} max={maxHeat} label={`${lk} x Impact ${ii + 1}`} />
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {sections.findings_detail && (() => {
                        const fNum = n++;
                        return (
                          <>
                            <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                              <SectionHead num={String(fNum)} title="Detailed Findings" />
                              <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.findings_detail)}</div>
                            </div>
                            {sections.severity_classification && (
                              <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                                <SectionHead num={`${fNum}.1`} title="Severity Classification" sub />
                                <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.severity_classification)}</div>
                              </div>
                            )}
                            {sections.risk_exposure && (
                              <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                                <SectionHead num={`${fNum}.2`} title="Risk Exposure Overview" sub />
                                <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.risk_exposure)}</div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {sections.recommendations && (
                        <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                          <SectionHead num={String(n++)} title="Recommendations & Action Plan" />
                          <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.recommendations)}</div>
                        </div>
                      )}

                      {sections.conclusion && (
                        <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                          <SectionHead num={String(n++)} title="Conclusion" />
                          <div style={{ background: "#f0fdf4", borderLeft: "4px solid #22c55e", padding: "16px 20px", borderRadius: "0 8px 8px 0", fontSize: "13px", lineHeight: 1.8, color: "#374151" }}>
                            {renderContent(sections.conclusion)}
                          </div>
                        </div>
                      )}

                      {Object.entries(sections).filter(([k]) => !knownKeys.has(k)).map(([key, content]) => (
                        <div key={key} style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                          <SectionHead num={String(n++)} title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
                          <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(content)}</div>
                        </div>
                      ))}
                    </>
                  );
                })()}

                {/* appendices */}
                {includeAppendix && (
                  <div style={{ pageBreakBefore: "always" }}>
                    <h2 style={{ fontSize: "20px", fontWeight: 700, borderBottom: `2px solid ${reportVisuals.primary}`, paddingBottom: "8px", marginBottom: "24px", color: reportVisuals.text }}>Appendices</h2>

                    <div className="no-break" style={{ marginBottom: "30px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix A — Statistics Summary</h3>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <tbody>
                          {[
                            ["Total Controls Assessed", totalControls],
                            [findingsLabel === "Finding Themes" ? "Finding Themes Identified" : "Total Findings Identified", totalFindings],
                            ["Compliance Score", `${complianceScore}%`],
                            ["Residual Risk Score", `${riskScore}%`],
                            ...(stats.team_findings ? [["Team Validated Findings", stats.team_findings]] : []),
                            ...(stats.solo_findings && stats.team_findings ? [["Solo Findings", stats.solo_findings]] : []),
                            ...Object.entries(stats.by_severity || {}).map(([k, v]) => [`${k} Severity Findings`, v]),
                            ["Report Generated", reportDate.toLocaleString()],
                            ["Audit Mode", report.audit_mode === "team" ? "Team (Multi-Agent)" : "Solo"],
                          ].map(([label, value], i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "6px 12px", fontWeight: 500, color: "#6b7280", width: "50%" }}>{label}</td>
                              <td style={{ padding: "6px 12px", fontWeight: 600, fontFamily: "monospace" }}>{String(value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="no-break" style={{ marginBottom: "30px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix B — Severity Definitions</h3>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead><tr style={{ background: "#f9fafb" }}>{["Level", "Definition", "Expected Response"].map((h) => (<th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: 700, fontSize: "11px", color: "#6b7280" }}>{h}</th>))}</tr></thead>
                        <tbody>
                          {[
                            { level: "Critical", color: "#991b1b", def: "Fundamental control failure presenting immediate risk of regulatory action, significant financial loss, or material compliance breach.", response: "Immediate remediation within 30 days. Board notification mandatory." },
                            { level: "High", color: "#ef4444", def: "Significant control weakness that could lead to regulatory findings, financial exposure, or compliance gaps.", response: "Remediation within 60 days. Senior management oversight required." },
                            { level: "Medium", color: "#f59e0b", def: "Moderate control deficiency that presents a potential risk if not addressed in a timely manner.", response: "Remediation within 90 days. Management action plan required." },
                            { level: "Low", color: "#3b82f6", def: "Minor control enhancement opportunity that represents best practice improvement.", response: "Address within next audit cycle. Monitor for deterioration." },
                            { level: "Observation", color: "#94a3b8", def: "Informational observation or enhancement suggestion with no immediate risk.", response: "Consider for future improvement. No formal tracking required." },
                          ].map((row, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "8px 12px" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 700, color: row.color }}>
                                  <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: row.color, display: "inline-block" }} />
                                  {row.level}
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", lineHeight: 1.5 }}>{row.def}</td>
                              <td style={{ padding: "8px 12px", lineHeight: 1.5, fontSize: "11px", color: "#6b7280" }}>{row.response}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {glossary.length > 0 && (
                      <div className="no-break" style={{ marginBottom: "30px" }}>
                        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix C — Glossary</h3>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                          <tbody>
                            {glossary.map(({ term, definition }, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                <td style={{ padding: "6px 12px", fontWeight: 700, color: "#2563eb", whiteSpace: "nowrap", verticalAlign: "top", width: "120px" }}>{term}</td>
                                <td style={{ padding: "6px 12px", lineHeight: 1.5 }}>{definition}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="no-break" style={{ marginBottom: "30px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>
                        Appendix {glossary.length > 0 ? "D" : "C"} — Distribution List & Sign-Off
                      </h3>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead><tr style={{ background: "#f9fafb" }}>{["Role", "Name", "Date", "Signature"].map((h) => (<th key={h} style={{ padding: "8px 12px", textAlign: h === "Role" || h === "Name" ? "left" : "center", borderBottom: "2px solid #e5e7eb", fontWeight: 700, fontSize: "11px", color: "#6b7280" }}>{h}</th>))}</tr></thead>
                        <tbody>
                          {distributionRoles.map((role, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "10px 12px", fontWeight: 500 }}>{role}</td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px dashed #d1d5db", minWidth: "150px" }} />
                              <td style={{ padding: "10px 12px", textAlign: "center", borderBottom: "1px dashed #d1d5db", minWidth: "100px" }} />
                              <td style={{ padding: "10px 12px", textAlign: "center", borderBottom: "1px dashed #d1d5db", minWidth: "120px" }} />
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* footer */}
                <div style={{ marginTop: "40px", paddingTop: "16px", borderTop: `2px solid ${reportVisuals.soft}`, display: "flex", justifyContent: "space-between", fontSize: "9px", color: "#9ca3af" }}>
                  <div>
                    <p style={{ margin: 0 }}>{auditType} Audit Report — {orgName}</p>
                    <p style={{ margin: "2px 0 0" }}>Reference: {refId}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ margin: 0 }}>Generated: {reportDate.toLocaleString()}</p>
                    <p style={{ margin: "2px 0 0" }}>Classification: Confidential</p>
                  </div>
                </div>

                <div style={{ marginTop: "20px", padding: "12px 16px", background: "#fefce8", border: "1px solid #fde68a", borderRadius: "8px", fontSize: "10px", color: "#92400e", lineHeight: 1.6 }}>
                  <strong>Disclaimer:</strong> This report has been prepared for the exclusive use of {orgName} management and its governing body. The findings and recommendations contained herein are based on information available at the time of the audit and represent the professional judgement of the audit team. This report should not be shared with external parties without prior written consent.
                </div>
              </div>
            </div>
          </div>

          {/* stats card */}
          <Card className="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900 dark:to-slate-800/50">
            <CardContent className="py-5">
              <div className="flex gap-8 text-center flex-wrap justify-center">
                {[
                  { value: totalControls, label: "Controls", color: "text-blue-600" },
                  { value: totalFindings, label: "Findings", color: "text-red-600" },
                  { value: `${complianceScore}%`, label: "Compliance", color: "text-blue-600" },
                  ...(stats.team_findings ? [{ value: stats.team_findings, label: "Team Validated", color: "text-blue-600" }] : []),
                  ...Object.entries(stats.by_severity || {}).map(([sev, count]) => ({
                    value: count, label: sev,
                    color: sev === "Critical" || sev === "High" ? "text-red-600" : "text-blue-600",
                  })),
                ].map((item, i) => (
                  <div key={i} className="min-w-[80px]">
                    <p className={`text-2xl font-bold font-mono leading-none ${item.color}`}>{item.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1 font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
